import { VisuallyHidden } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";

import {
  useListBrands,
  useListMembers,
  useListProducts,
} from "../../../api/generated/endpoints/teams/teams";
import type { TeamRole } from "../../../api/generated/model";
import { accessScopeQuery, findTeam } from "../../../api/session";
import { useJobList } from "../../../platform/jobs/job-list";
import { managesBrands, managesMembers } from "../../../platform/permissions";
import { useStreamStatus } from "../../../platform/stream-status";
import { Soon } from "../../../ui/Soon";
import classes from "./getting-started.module.css";

/** The brand's newest jobs, Pro Home's Continue read (its key): one that succeeded is a first ad. */
const READ = 20;

/** Where a step's Go leads: the brand's and the team's pages are Pro's until Simple has its own. */
type Place =
  | "/teams/$teamId/brands/$brandId/pro/brand/edit"
  | "/teams/$teamId/brands/$brandId/pro/products/new"
  | "/teams/$teamId/brands/$brandId/simple/make"
  | "/teams/$teamId/brands/$brandId/pro/settings";

type Key = "brand" | "product" | "first" | "publish" | "meta" | "team";

interface Step {
  key: Key;
  label: string;
  /** Where Go leads; `null`: its feature is not built (Coming soon). */
  place: Place | null;
  /** Who may do it, by the access scope's role (A2's matrix); none: every member. */
  may?: (role: TeamRole) => boolean;
}

/** The prototype's six, in its order and words. */
const STEPS: readonly Step[] = [
  {
    key: "brand",
    label: "Set up your brand",
    place: "/teams/$teamId/brands/$brandId/pro/brand/edit",
    may: managesBrands,
  },
  {
    key: "product",
    label: "Add your product",
    place: "/teams/$teamId/brands/$brandId/pro/products/new",
    may: managesBrands,
  },
  { key: "first", label: "Make your first ads", place: "/teams/$teamId/brands/$brandId/simple/make" },
  // Publishing and Meta's connection need the ads area (#253).
  { key: "publish", label: "Publish your first ad", place: null },
  { key: "meta", label: "Connect Meta", place: null },
  { key: "team", label: "Invite your team", place: "/teams/$teamId/brands/$brandId/pro/settings", may: managesMembers },
];

/** Each step done or not, from reads the app already makes; not read yet is not done. */
function useDone(teamId: string, brandId: string): Readonly<Record<Key, boolean>> {
  // A brand kit: its logo or a colour, as the prototype's onboarding fills them from the website.
  const kit = useListBrands(teamId, {
    query: {
      select: (list) => {
        const brand = list.find((each) => each.id.toLowerCase() === brandId.toLowerCase());
        return brand !== undefined && (brand.logo_url !== null || brand.colors.length > 0);
      },
    },
  }).data;
  const products = useListProducts(teamId, brandId, undefined, {
    query: { select: (list) => list.length > 0 },
  }).data;
  const jobs = useJobList(teamId, { status: "all", brandId, pageSize: READ }, useStreamStatus());
  const made = jobs.data?.pages[0]?.items.some((job) => job.status === "succeeded");
  const team = useListMembers(teamId, { query: { select: (list) => list.length > 1 } }).data;
  return {
    brand: kit === true,
    product: products === true,
    first: made === true,
    publish: false,
    meta: false,
    team: team === true,
  };
}

/**
 * The prototype's Getting started, docked at the foot of Simple's nav (spec
 * ui-perf/simple-getting-started): six steps, each done from what the app already reads, with
 * its count and progress; an undone step's Go opens its place, or says to ask an admin where the
 * person's role cannot do it. `onNavigate` closes the phone's drawer, as the nav's links do.
 */
export function GettingStarted(props: { teamId: string; brandId: string; onNavigate?: (() => void) | undefined }) {
  const { teamId, brandId, onNavigate } = props;
  const done = useDone(teamId, brandId);
  const role = useQuery({ ...accessScopeQuery(), select: (scope) => findTeam(scope, teamId)?.role }).data;
  const count = STEPS.filter((step) => done[step.key]).length;
  return (
    <details className={classes.card}>
      <summary className={classes.summary}>
        <b className={classes.title}>Getting started</b>
        <span className={classes.count}>
          {count}/{STEPS.length}
        </span>
      </summary>
      <progress className={classes.progress} aria-label="Getting started" value={count} max={STEPS.length} />
      <ul className={classes.steps}>
        {STEPS.map((step) => (
          <li key={step.key} className={classes.step} data-done={done[step.key] || undefined}>
            <span className={classes.mark} aria-hidden>
              {done[step.key] ? <Check size={12} strokeWidth={3} /> : null}
            </span>
            <span className={classes.label}>
              {step.label}
              {done[step.key] ? <VisuallyHidden>: done</VisuallyHidden> : null}
            </span>
            {done[step.key] ? null : step.place !== null && role !== undefined && step.may?.(role) === false ? (
              // The page would refuse its save (the API checks again): the person's way is an admin.
              <span className={classes.ask}>Ask an admin</span>
            ) : step.place === null ? (
              <Soon>
                {(props) => (
                  <button type="button" className={classes.go} aria-label={`Go: ${step.label}`} {...props}>
                    Go
                  </button>
                )}
              </Soon>
            ) : (
              // Six "Go"s: each named by its step for a screen reader.
              <Link
                to={step.place}
                params={{ teamId, brandId }}
                className={classes.go}
                aria-label={`Go: ${step.label}`}
                onClick={onNavigate}
              >
                Go
              </Link>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
